// ============================================================================
// InformedConsentEngine — Motor de Consentimentos Informados
// UX: CRM modal → Petição vinculada → Gerar/Editar → Salvar HTML → Publicar PDF
// Padrão dual idêntico ao DeclarationEngine (Salvar HTML + Publicar PDF)
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, auth } from '../../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../../config/Config';
import {
  watchClientPetitions,
  saveInformedConsent,
  publishInformedConsent,
} from '../../services/InformedConsentRepository.js';
import { DocumentShell, ConfigSection } from '../../components/layout/DocumentShell';
import { CrmClientPickerModal } from '../../components/CrmClientPickerModal';
import { ShieldCheck, CheckCircle, AlertCircle, Sparkles, Download, Trash2 } from 'lucide-react';
import { fetchWithWarmup } from '../../utils/backendWarmup';

const appId = FIREBASE_CONFIG.projectId;
const storage = getStorage();
const DOCS_COL = `artifacts/${appId}/public/data/documents`;
const BACKEND_URL = String(import.meta?.env?.VITE_BACKEND_URL || 'https://rda-backend-thuq.onrender.com').trim().replace(/\/+$/, '');

function InformedConsentEngine() {
  // ── State ──────────────────────────────────────────────────────────────────
  const [showImport, setShowImport] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [petitions, setPetitions] = useState([]);
  const [selectedPetitionId, setSelectedPetitionId] = useState('');
  const [selectedPetitionTitle, setSelectedPetitionTitle] = useState('');
  const [openSection, setOpenSection] = useState('client');

  const [resumo, setResumo] = useState('');
  const [pedidos, setPedidos] = useState('');
  const [riscos, setRiscos] = useState('');
  const [alternativas, setAlternativas] = useState('');
  const [observacoes, setObservacoes] = useState('');

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [error, setError] = useState('');
  const [documentId, setDocumentId] = useState('');

  const clientId = String(clientData?.id || '').trim();
  const clientName = String(clientData?.qualification?.physical?.name || clientData?.fullName || clientData?.name || '').trim();

  const showToast = useCallback((msg) => { setToastMsg(msg); setTimeout(() => setToastMsg(''), 5000); }, []);
  const hasContent = !!(resumo.trim() || pedidos.trim() || riscos.trim());

  // ── Handle client import from CRM ─────────────────────────────────────────
  const handleImportClient = useCallback((picked) => {
    if (!picked) return;
    setClientData(picked);
    setShowImport(false);
    setOpenSection('peticao');
    setSelectedPetitionId('');
    setSelectedPetitionTitle('');
    setDocumentId('');
  }, []);

  // ── Load petitions for selected client ─────────────────────────────────────
  useEffect(() => {
    if (!clientId) { setPetitions([]); return; }
    const unsub = watchClientPetitions(
      clientId,
      (docs) => { setPetitions(docs); },
      () => setPetitions([])
    );
    return unsub;
  }, [clientId]);

  // ── Generate HTML body ─────────────────────────────────────────────────────
  const generateHtml = useCallback(() => {
    const sections = [];
    if (resumo.trim()) sections.push(`<h3 style="color:#20243B;font-size:14px;margin:16px 0 6px;">Resumo da Petição</h3><p style="font-size:12px;line-height:1.7;color:#374151;">${resumo.trim().replace(/\n/g, '<br/>')}</p>`);
    if (pedidos.trim()) sections.push(`<h3 style="color:#20243B;font-size:14px;margin:16px 0 6px;">Pedidos Formulados</h3><ul style="font-size:12px;line-height:1.7;color:#374151;margin:0;padding-left:20px;">${pedidos.trim().split('\n').filter(Boolean).map(p => `<li>${p}</li>`).join('')}</ul>`);
    if (riscos.trim()) sections.push(`<h3 style="color:#20243B;font-size:14px;margin:16px 0 6px;">Riscos e Consequências</h3><ul style="font-size:12px;line-height:1.7;color:#374151;margin:0;padding-left:20px;">${riscos.trim().split('\n').filter(Boolean).map(r => `<li>${r}</li>`).join('')}</ul>`);
    if (alternativas.trim()) sections.push(`<h3 style="color:#20243B;font-size:14px;margin:16px 0 6px;">Alternativas Disponíveis</h3><ul style="font-size:12px;line-height:1.7;color:#374151;margin:0;padding-left:20px;">${alternativas.trim().split('\n').filter(Boolean).map(a => `<li>${a}</li>`).join('')}</ul>`);
    if (observacoes.trim()) sections.push(`<h3 style="color:#20243B;font-size:14px;margin:16px 0 6px;">Observações</h3><p style="font-size:12px;line-height:1.7;color:#374151;">${observacoes.trim().replace(/\n/g, '<br/>')}</p>`);
    return `
      <div style="font-family:'Roboto',Arial,sans-serif;max-width:700px;margin:0 auto;padding:24px;">
        <h1 style="color:#20243B;font-size:18px;border-bottom:2px solid #C5A059;padding-bottom:8px;margin-bottom:16px;">TERMO DE CONSENTIMENTO INFORMADO</h1>
        <p style="font-size:12px;color:#666;margin-bottom:20px;">Vinculado à petição: <strong>${selectedPetitionTitle || '—'}</strong><br/>Cliente: <strong>${clientName || '—'}</strong></p>
        ${sections.join('\n')}
        <hr style="margin:24px 0;border:none;border-top:1px solid #e5e7eb;"/>
        <p style="font-size:11px;color:#666;line-height:1.6;">Este documento foi elaborado em conformidade com o Código de Ética e Disciplina da OAB (Art. 8º) e a Lei Geral de Proteção de Dados (Lei 13.709/2018, Art. 7º, I). Ao aceitar, o(a) cliente declara ter sido informado(a) sobre os riscos, alternativas e consequências da medida judicial proposta.</p>
      </div>`;
  }, [resumo, pedidos, riscos, alternativas, observacoes, selectedPetitionTitle, clientName]);

  // ── Salvar HTML (rascunho) ─────────────────────────────────────────────────
  const handleSaveHtml = useCallback(async () => {
    if (!clientId || !selectedPetitionId) { setError('Selecione cliente e petição vinculada.'); return; }
    if (!hasContent) { setError('Preencha ao menos o resumo, pedidos ou riscos.'); return; }
    if (isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      const htmlContent = generateHtml();
      const title = `Consentimento Informado — ${selectedPetitionTitle || 'Petição'}`;
      const parentPet = petitions.find((p) => p.id === selectedPetitionId);
      const cadernoId = parentPet?.cadernoId || '';
      const actorName = String(auth?.currentUser?.displayName || auth?.currentUser?.email || 'Operador').trim();
      const payload = {
        type: 'INFORMED_CONSENT',
        format: 'html',
        title,
        clientId,
        clientName,
        cadernoId,
        cadernoName: parentPet?.cadernoName || '',
        parentDocumentId: selectedPetitionId,
        parentDocumentTitle: selectedPetitionTitle,
        htmlContent,
        consentSummary: {
          resumoPeticao: resumo.trim(),
          pedidos: pedidos.trim().split('\n').filter(Boolean),
          riscos: riscos.trim().split('\n').filter(Boolean),
          alternativas: alternativas.trim().split('\n').filter(Boolean),
          observacoes: observacoes.trim(),
        },
        status: 'DRAFT',
        clientVisible: false,
        requiresConsent: true,
        consentStatus: 'pending',
        category: 'FORMAL_DOCUMENT',
        module: 'petitions',
        publication: { source: 'consent_engine', format: 'html', publishedAtIso: new Date().toISOString(), publishedBy: actorName, storageMode: 'html_archive' },
      };

      const docId = await saveInformedConsent(payload, clientId, selectedPetitionId, documentId, auth?.currentUser?.email || null);
      setDocumentId(docId);
      showToast('Consentimento salvo no prontuário. NÃO visível ao cliente ainda. Publique o PDF para liberar.');
    } catch (err) {
      console.error('[InformedConsentEngine] Save HTML failed:', err);
      setError(`Erro ao salvar: ${err?.message || 'Desconhecido'}`);
    } finally {
      setIsSaving(false);
    }
  }, [clientId, clientName, selectedPetitionId, selectedPetitionTitle, petitions, resumo, pedidos, riscos, alternativas, observacoes, hasContent, documentId, isSaving, generateHtml, showToast]);

  // ── Publicar PDF (com letterhead) ──────────────────────────────────────────
  const handlePublishPdf = useCallback(async () => {
    if (!documentId) { setError('Salve o HTML primeiro antes de publicar o PDF.'); return; }
    if (isPublishing) return;
    setIsPublishing(true);
    setError('');
    try {
      const parentPet = petitions.find((p) => p.id === selectedPetitionId);
      const cadernoId = parentPet?.cadernoId || '';
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const stem = `[${ts}]-Consentimento-Informado-${clientName.replace(/\s+/g, '-').toUpperCase()}`;
      const fileName = `${stem}.pdf`;
      const storagePath = `artifacts/${appId}/public/documents/consents/${clientId}/${fileName}`;
      const htmlContent = generateHtml();

      const fullHtml = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8"/>
  <title>Consentimento Informado — ${clientName}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    @page { size: A4; margin: 22mm 18mm; }
    html, body { margin: 0; padding: 0; font-family: 'Roboto', Arial, sans-serif; color: #20243B; -webkit-font-smoothing: antialiased; }
    .consent-page { max-width: 700px; margin: 0 auto; padding: 40px 0; }
    @media print { .consent-page { max-width: none; padding: 0; } }
  </style>
</head>
<body>
  <div class="consent-page">
    ${htmlContent}
  </div>
</body>
</html>`;

      let fileUrl = '';
      let downloadUrl = '';
      try {
        const resp = await fetchWithWarmup(`${BACKEND_URL}/drive/generate-pdf?t=${Date.now()}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ html: fullHtml, fileName }),
        }, {
          onWaking: () => showToast('Backend acordando... aguarde até 40s na primeira vez.'),
          onRetry: (attempt, max) => showToast(`Tentativa ${attempt + 1}/${max}... aguarde.`),
        });
        if (resp.ok) {
          const pdfBlob = await resp.blob();
          const sRef = storageRef(storage, storagePath);
          await uploadBytes(sRef, pdfBlob, { contentType: 'application/pdf' });
          downloadUrl = await getDownloadURL(sRef);
          fileUrl = downloadUrl;
        }
      } catch (pdfErr) {
        console.warn('[InformedConsentEngine] PDF via backend failed (non-blocking):', pdfErr?.message);
      }

      await publishInformedConsent(documentId, {
        status: 'PUBLISHED',
        format: 'pdf',
        fileUrl,
        downloadUrl,
        storagePath: fileUrl ? storagePath : '',
        fileName,
        mimeType: 'application/pdf',
        extension: 'pdf',
        clientVisible: true,
        cadernoId: cadernoId || null,
        cadernoName: parentPet?.cadernoName || null,
        publication: {
          source: 'consent_engine',
          format: 'pdf',
          publishedAtIso: new Date().toISOString(),
          publishedBy: String(auth?.currentUser?.displayName || auth?.currentUser?.email || 'Operador').trim(),
          storageMode: fileUrl ? 'pdf_archive' : 'html_archive',
        },
      }, auth?.currentUser?.email || null);

      showToast(fileUrl
        ? 'Consentimento publicado em PDF — visível no portal do cliente!'
        : 'Consentimento publicado (HTML). Backend PDF indisponível — verifique o Render.'
      );
    } catch (err) {
      console.error('[InformedConsentEngine] Publish PDF failed:', err);
      setError(`Erro ao publicar PDF: ${err?.message || 'Desconhecido'}`);
    } finally {
      setIsPublishing(false);
    }
  }, [documentId, isPublishing, clientId, clientName, selectedPetitionId, petitions, generateHtml, showToast]);

  // ── Action bar (2 botões: Salvar HTML + Publicar PDF) ─────────────────────
  const canSave = !!(clientId && selectedPetitionId && hasContent);
  const canPublish = !!documentId;
  const actionBar = (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleSaveHtml}
        disabled={isSaving || !canSave}
        className={`w-full flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap border transition-colors shadow-sm ${
          canSave ? 'border-violet-200 bg-white text-violet-700 hover:bg-violet-50' : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
        }`}
      >
        <CheckCircle className="w-3 h-3" />
        {isSaving ? 'Salvando...' : 'Salvar HTML'}
      </button>
      <button
        type="button"
        onClick={handlePublishPdf}
        disabled={isPublishing || !canPublish}
        className={`w-full flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold whitespace-nowrap border transition-colors shadow-sm ${
          canPublish ? 'border-rose-200 bg-white text-rose-700 hover:bg-rose-50' : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
        }`}
      >
        <ShieldCheck className="w-3 h-3" />
        {isPublishing ? 'Publicando...' : 'Publicar PDF'}
      </button>
      {error && (
        <div className="flex items-center gap-2 p-2 rounded-lg border text-[10px] font-medium bg-red-50 border-red-200 text-red-700">
          <AlertCircle className="w-3 h-3 flex-shrink-0" /> {error}
        </div>
      )}
      {toastMsg && (
        <div className="flex items-center gap-2 p-2 rounded-lg border text-[10px] font-medium bg-emerald-50 border-emerald-200 text-emerald-700">
          <CheckCircle className="w-3 h-3 flex-shrink-0" /> {toastMsg}
        </div>
      )}
    </div>
  );

  // ── Center panel ───────────────────────────────────────────────────────────
  const centerPanel = (
    <div className="flex-1 flex flex-col items-center justify-start overflow-y-auto bg-gray-100 p-6">
      {!clientId ? (
        <div className="w-full max-w-3xl flex flex-col items-center justify-center py-20 text-center">
          <i className="fas fa-user-circle text-4xl text-gray-200 mb-4" />
          <p className="text-sm font-bold text-gray-500">Selecione um cliente no painel lateral</p>
          <p className="text-xs text-gray-400 mt-1">Clique em "Importar do CRM" para buscar o cliente.</p>
        </div>
      ) : !selectedPetitionId ? (
        <div className="w-full max-w-3xl flex flex-col items-center justify-center py-20 text-center">
          <i className="fas fa-gavel text-4xl text-gray-200 mb-4" />
          <p className="text-sm font-bold text-gray-500">Selecione uma petição vinculada</p>
          <p className="text-xs text-gray-400 mt-1">Escolha a petição para a qual deseja gerar o consentimento informado.</p>
        </div>
      ) : (
        <div className="w-full max-w-3xl space-y-4">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
              <Sparkles className="w-3 h-3 inline mr-1" />Resumo da Petição (para o cliente entender)
            </label>
            <textarea value={resumo} onChange={(e) => setResumo(e.target.value)} placeholder="Explique de forma simples o que a petição faz e por que está sendo ajuizada..." rows={4} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 resize-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Pedidos Formulados (um por linha)</label>
            <textarea value={pedidos} onChange={(e) => setPedidos(e.target.value)} placeholder="Liminar para garantir a posse&#10;Declaração de nulidade do ato" rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 resize-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Riscos e Consequências (um por linha)</label>
            <textarea value={riscos} onChange={(e) => setRiscos(e.target.value)} placeholder="Possibilidade de indeferimento&#10;Custas em caso de improcedência" rows={3} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 resize-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Alternativas Disponíveis (um por linha)</label>
            <textarea value={alternativas} onChange={(e) => setAlternativas(e.target.value)} placeholder="Recurso administrativo&#10;Mediação extrajudicial" rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 resize-none" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">Observações Adicionais (opcional)</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} placeholder="Informações extras, disclaimers..." rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30 resize-none" />
          </div>
          {hasContent && (
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2">
                <ShieldCheck className="w-3 h-3 text-emerald-600" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Preview do Consentimento</span>
              </div>
              <div className="p-4" dangerouslySetInnerHTML={{ __html: generateHtml() }} />
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DocumentShell
        title="Motor de Consentimentos"
        tabs={[
          { id: 'client', label: 'Cliente' },
          { id: 'peticao', label: 'Petição' },
        ]}
        activeTab={openSection}
        onTabChange={setOpenSection}
        actionBar={actionBar}
        centerPanel={centerPanel}
      >
        <ConfigSection id="client" title="Qualificação do Cliente" icon="fa-user" openSection={openSection} setOpenSection={setOpenSection}>
          <div className="space-y-2.5">
            <div className="flex gap-2">
              <button
                onClick={() => setShowImport(true)}
                className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition-colors shadow-sm"
                type="button"
              >
                <Download className="w-3 h-3" /> IMPORTAR DO CRM
              </button>
            </div>
            {clientData && (
              <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-[0.04em]">CLIENTE SELECIONADO</div>
                  <button
                    onClick={() => { setClientData(null); setSelectedPetitionId(''); setSelectedPetitionTitle(''); setDocumentId(''); }}
                    className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                    type="button"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
                <div className="mt-2 text-[11px] font-bold text-[#20243B]">{clientName || 'Sem nome'}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">{clientData?.qualification?.physical?.cpf || ''}</div>
              </div>
            )}
          </div>
        </ConfigSection>

        <ConfigSection id="peticao" title="Petição Vinculada" icon="fa-gavel" openSection={openSection} setOpenSection={setOpenSection}>
          {!clientId ? (
            <p className="text-[10px] text-gray-400 italic">Importe um cliente do CRM primeiro.</p>
          ) : petitions.length === 0 ? (
            <p className="text-[10px] text-gray-400 italic">Nenhuma petição publicada para este cliente. Publique uma petição primeiro.</p>
          ) : (
            <div className="space-y-1.5">
              {petitions.map((pet) => (
                <button
                  key={pet.id}
                  type="button"
                  onClick={() => { setSelectedPetitionId(pet.id); setSelectedPetitionTitle(pet.title || 'Petição'); setDocumentId(''); }}
                  className={`w-full text-left px-2 py-1.5 rounded text-[11px] transition-colors ${
                    selectedPetitionId === pet.id
                      ? 'bg-emerald-50 text-[#20243B] font-bold border border-emerald-200'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                >
                  <i className="fas fa-gavel mr-1.5 text-[9px] text-gray-400" />
                  {pet.title || 'Petição'}
                </button>
              ))}
            </div>
          )}
        </ConfigSection>
      </DocumentShell>

      {showImport && createPortal(
        <CrmClientPickerModal isOpen={showImport} onClose={() => setShowImport(false)} onSelect={handleImportClient} db={db} appId={appId} title="Base de Clientes (CRM)" />,
        document.body
      )}
    </>
  );
}

// --- FACTORY EXPORT (padrao CDAS) ---
export const InformedConsentFactory = () => <InformedConsentEngine />;
export default InformedConsentEngine;
